import { useState } from 'react';
import { Calculator, X, AlertTriangle } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { DIRECTIONAL_BASELINE_LABEL } from '@siliconx/shared';
import type { BasisSource, CalculationBasis, CalculationBasisInput } from '@siliconx/shared';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { DataTable } from '@siliconx/shared/ui/DataTable';

/**
 * SIL-301 (W4, financials-hifi-flow-spec.md Cross-cutting) — the ONE shared
 * "show the math" drawer for `CalculationBasis`. Built first because it
 * gates every other W4 UI issue's basis affordance: book-vs-fmv,
 * depreciation, opportunity, and scenario-compare (and any later screen)
 * all drop a single `<CalculationBasisTrigger>` next to a stat tile or
 * chart header and get inputs table, formula reference, constants, and the
 * candid-fallback callout for free — no bespoke rendering per screen.
 *
 * Usage:
 *
 * ```tsx
 * <CalculationBasisTrigger title="Net Book Value" basis={row.basis} />
 * ```
 *
 * `basis` is `CalculationBasis | null | undefined` — the trigger renders
 * nothing when there's no basis to show (endpoint hasn't attached one for
 * this row), so it's always safe to render inline without an extra guard.
 */

// ---------------------------------------------------------------------------
// Source enum -> human label. Lives beside the component per spec
// Cross-cutting ("never free text" for the enum itself — this map is the
// ONLY place the closed `BasisSource` enum gets prose).
// ---------------------------------------------------------------------------

export const BASIS_SOURCE_LABELS: Record<BasisSource, string> = {
  erp: 'ERP register',
  manual_entry: 'Manual entry',
  sku_launch_price_estimate: 'SKU launch price (estimate)',
  fmv_snapshot: 'FMV snapshot',
  market_comps: 'Market comps',
  telemetry: 'Telemetry',
  policy_class_override: 'Class policy override',
  policy_org_default: 'Org default policy',
  platform_default: 'Platform default',
  oem_nameplate_spec: 'OEM nameplate spec (estimate)',
  // SIL-436 — carbon-kernel basis sources (scenario-compare's carbon block).
  asset_use_phase_snapshot: 'Use-phase snapshot',
  grid_intensity_factor: 'Grid intensity factor',
  org_sustainability_config: 'Org sustainability config',
  sku_carbon_footprint: 'SKU carbon footprint',
};

/**
 * Method-specific fallback copy, keyed by input NAME rather than source.
 *
 * `BasisSource` is a closed enum describing where a value came from, and
 * sometimes two inputs share a source while their fallbacks mean completely
 * different things. The suggested-action endpoints are both `fmv_snapshot`
 * — they genuinely are FMV snapshots — but the thing being disclosed is not
 * "no recent snapshot", it is "we compared two of them and called it a
 * trend". Source alone cannot say that, so a name lookup runs first.
 *
 * Add an entry here only when the source-level copy would be actively
 * misleading; the source switch stays the default.
 */
const FALLBACK_COPY_BY_INPUT_NAME: Record<string, string> = {
  trendStartFmvMedUsd:
    'the first of the 18-month trend points - one sampled month, not a fitted trend',
  trendEndFmvMedUsd:
    'the last of the 18-month trend points - one sampled month, not a fitted trend',
  trendChangePct:
    'measured between the first and last trend point only - a class that fell and recovered reads as unchanged, and the band edges are exact cutoffs',
};

/**
 * Candid-fallback copy per source (spec Cross-cutting "candid estimate
 * fallbacks ... never hide the fallback"). This is the point of the whole
 * feature — plain, visible, factual. Market-comp suppression gets its own
 * dedicated copy per the "Market-comp disclosure policy" (spec: basis
 * candidly reads "insufficient market depth - estimate widened").
 */
function fallbackCopyFor(input: CalculationBasisInput): string {
  const byName = FALLBACK_COPY_BY_INPUT_NAME[input.name];
  if (byName) return byName;

  switch (input.source) {
    case 'sku_launch_price_estimate':
      return 'purchase price estimated from launch list price - no ERP value';
    case 'market_comps':
      return 'insufficient market depth - estimate widened';
    case 'policy_org_default':
      return 'no per-class policy override - falling back to the organization default';
    case 'platform_default':
      return 'no organization policy set - falling back to the platform default';
    case 'fmv_snapshot':
      return 'no recent FMV snapshot - using the last available value';
    case 'telemetry':
      return 'no telemetry available - using the rated-spec fallback';
    case 'policy_class_override':
      return 'a class-level policy override is applied to this figure';
    case 'erp':
    case 'manual_entry':
    default:
      return `${humanizeInputName(input.name)} is estimated, not sourced from an authoritative record`;
  }
}

/** camelCase input/constant name -> "Title Case with spaces". */
function humanizeInputName(name: string): string {
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** A `market_comps` input suppressed below the k-anonymity floor renders a
 * candid "insufficient market depth" value — NEVER an empty cell or a bare
 * em dash (spec Cross-cutting "never an empty section"). */
function isSuppressedComps(input: CalculationBasisInput): boolean {
  return input.source === 'market_comps' && (input.value === null || input.isFallback);
}

/**
 * The DTO doesn't carry a unit hint (dollars vs. count vs. percent), so this
 * is a plain formatted number, not a currency string — same convention as
 * the constants table below. Screens that want a unit-specific rendering of
 * a KNOWN input can still format the raw value themselves before handing it
 * to this component; this generic fallback is what the shared drawer shows
 * for everything else.
 */
function formatInputValue(input: CalculationBasisInput): string {
  if (isSuppressedComps(input)) return 'insufficient market depth - estimate widened';
  if (input.value === null) return '—';
  return input.value.toLocaleString();
}

// ---------------------------------------------------------------------------
// Trigger — the info affordance. Owns its own open/close state so any
// screen can drop it next to a stat tile or chart header with one line.
// ---------------------------------------------------------------------------

export function CalculationBasisTrigger({
  title,
  basis,
  align = 'right',
}: {
  /** The figure/chart this basis explains, e.g. "Net Book Value". Reused
   * as the drawer's title. */
  title: string;
  basis: CalculationBasis | null | undefined;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);

  if (!basis) return null;

  const anyFallback = basis.inputs.some((i) => i.isFallback || i.isDirectional);

  return (
    <>
      {/* `Calculator` (not `Info`) — this is the ONE thing on the page that
       * differentiates from `InfoTooltip`'s plain inline explanation:
       * "show the math" opens a full drawer with the inputs/formula/
       * constants, a different affordance than a hover popover, so it
       * shouldn't share the same glyph. `p-1.5` (was `p-0.5`) brings the
       * hit target to size-4 + 12px padding = 26px, clearing the 24px
       * floor — previously ~18px, the smallest target on the app's
       * signature audit affordance. Fallback presence is amber (color)
       * PLUS a filled corner dot (shape) so it doesn't rely on color alone. */}
      <button
        type="button"
        aria-label={`Show the math for ${title}`}
        onClick={() => setOpen(true)}
        className={`relative inline-flex items-center justify-center rounded-full p-1.5 ${
          anyFallback ? 'text-amber-500 hover:text-amber-600' : 'text-zinc-400 hover:text-zinc-600'
        }`}
        title="Show the math"
      >
        <Calculator className="size-4" />
        {anyFallback && (
          <span aria-hidden="true" className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-amber-500" />
        )}
      </button>
      {open && (
        <CalculationBasisDrawer
          title={title}
          basis={basis}
          onClose={() => setOpen(false)}
          align={align}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Drawer — inputs table, formula reference, constants, candid-fallback
// callout. Right-side slide-over, same overlay/panel pattern as
// apps/customer/src/routes/team.tsx's member drawer.
// ---------------------------------------------------------------------------

const INPUT_COLUMNS: ColumnDef<CalculationBasisInput, unknown>[] = [
  {
    id: 'name',
    header: 'Input',
    cell: (ctx) => (
      <span className="align-top font-medium text-zinc-700">
        {humanizeInputName(ctx.row.original.name)}
      </span>
    ),
  },
  {
    id: 'value',
    header: 'Value',
    cell: (ctx) => {
      const input = ctx.row.original;
      return (
        <span
          className={`align-top tabular-nums ${
            isSuppressedComps(input)
              ? 'italic text-amber-700'
              : input.isFallback
                ? 'text-amber-700'
                : 'text-zinc-700'
          }`}
        >
          {formatInputValue(input)}
        </span>
      );
    },
  },
  {
    id: 'source',
    header: 'Source',
    cell: (ctx) => {
      const input = ctx.row.original;
      return (
        <span className="align-top text-zinc-500">
          {BASIS_SOURCE_LABELS[input.source]}
          {input.isFallback && (
            <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
              estimated
            </span>
          )}
          {/* SIL-1828 (R2): label only - the server flag, never re-derived here,
              and never any component detail. */}
          {input.isDirectional && (
            <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
              Directional
            </span>
          )}
        </span>
      );
    },
  },
  {
    id: 'asOf',
    header: 'As of',
    cell: (ctx) => (
      <span className="align-top text-zinc-400">
        {new Date(ctx.row.original.asOf).toLocaleDateString()}
      </span>
    ),
  },
];

export function CalculationBasisDrawer({
  title,
  basis,
  onClose,
}: {
  title: string;
  basis: CalculationBasis;
  onClose: () => void;
  align?: 'left' | 'right';
}) {
  const fallbackInputs = basis.inputs.filter((i) => i.isFallback);
  const directionalInputs = basis.inputs.filter((i) => i.isDirectional);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <div
        role="dialog"
        aria-label={`Show the math — ${title}`}
        className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <SectionLabel>
              Show the math
            </SectionLabel>
            <h2 className="mt-0.5 text-base font-semibold text-zinc-900">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Candid-fallback callout — visually distinct, never softened. */}
        {fallbackInputs.length > 0 && (
          <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div className="space-y-1 text-sm text-amber-900">
              {fallbackInputs.map((i) => (
                <p key={i.name}>
                  <span className="font-medium">{humanizeInputName(i.name)}</span> — {fallbackCopyFor(i)}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* SIL-1828 (R2): directional callout - label only. */}
        {directionalInputs.length > 0 && (
          <div
            role="note"
            className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div className="space-y-1 text-sm text-amber-900">
              {directionalInputs.map((i) => (
                <p key={i.name}>
                  <span className="font-medium">{humanizeInputName(i.name)}</span> - includes a{' '}
                  {DIRECTIONAL_BASELINE_LABEL.toLowerCase()} for hardware with no market comps yet. Treat as directional, not exact.
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Inputs table */}
        <div className="mt-5">
          <SectionLabel>Inputs</SectionLabel>
          <div className="mt-2">
            <DataTable data={basis.inputs} columns={INPUT_COLUMNS} />
          </div>
        </div>

        {/* Constants */}
        {basis.constants.length > 0 && (
          <div className="mt-5">
            <SectionLabel>
              Constants
            </SectionLabel>
            <table className="mt-2 w-full text-sm">
              <tbody className="divide-y divide-zinc-100">
                {basis.constants.map((c) => (
                  <tr key={c.name}>
                    <td className="py-1.5 pr-2 text-zinc-700">{humanizeInputName(c.name)}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-zinc-700">{c.value}</td>
                    <td className="py-1.5 text-right text-zinc-400">{BASIS_SOURCE_LABELS[c.source]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Formula reference */}
        <div className="mt-5 border-t border-zinc-100 pt-4">
          <SectionLabel>Formula</SectionLabel>
          <code className="mt-1.5 block break-all rounded-md bg-zinc-50 px-2.5 py-1.5 font-mono text-xs text-zinc-600">
            {basis.formulaId}
          </code>
        </div>
      </div>
    </div>
  );
}
